/**
 * Generated analytics event types and runtime constants.
 * Auto-generated - do not edit manually.
 */

/* eslint-disable -- auto-generated; lint findings live with upstream schema authors */

/**
 * Event fired when a user updates their account settings. Tracks which settings were changed.
 */
export interface IAccountSettingsUpdate {
	type: 'account.settings_update';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * Object indicating which settings were updated
		 */
		settings_updated: {
			/**
			 * Whether email was updated
			 */
			email?: boolean;
			/**
			 * Whether password was updated
			 */
			password?: boolean;
		};
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Triggered when a user is created
 */
export interface IAccountUserCreate {
	type: 'account.user_create';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * ID for the student
		 */
		user_id: string;
		/**
		 * ID for the parent member
		 */
		member_id: string;
		/**
		 * Birth month of the student (YYYY-MM-01)
		 */
		birth_month: string;
		/**
		 * ID for the chosen avatar
		 */
		avatar: string;
		/**
		 * Whether the student has a common or custom pronunciation chosen
		 */
		pronunciation: 'common' | 'custom';
		/**
		 * Reading grade level of the student
		 */
		reading_grade?: string;
		/**
		 * Math grade level of the student
		 */
		math_grade?: string;
		/**
		 * Indicates if the name was updated
		 */
		name_updated?: boolean;
		/**
		 * Topics the student is interested in
		 */
		topics?: string[];
		/**
		 * Levels the parent sets during onboarding (to be further developed)
		 */
		levels?: {};
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Base event specification for application events. These fields are on every event
 */
export interface IBaseEvent {
	type: 'base_event';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * uuid of the event, generated by the event logger
		 */
		uuid: string;
		/**
		 * Client timestamp in milliseconds
		 */
		timestamp: string;
		/**
		 * Client device_id
		 */
		device_id: string;
		/**
		 * Version of the event format
		 */
		specversion: string;
		/**
		 * A dot delimited name for the type of event
		 */
		event_type: string;
		/**
		 * The pipeline or product name where the event is being fired from
		 */
		source: string;
		/**
		 * The source version (app version / api version)
		 */
		source_version: string;
		/**
		 * The pipeline
		 */
		pipeline: string;
		data: {
			/**
			 * The AOFL product that the user is using
			 */
			aofl_product: string;
			[k: string]: unknown;
		};
		/**
		 * The pipeline context for the event
		 */
		pipeline_context: {
			client: {
				/**
				 * The platform of the client
				 */
				client_platform?: string;
				/**
				 * Whether the event is from an automated test
				 */
				is_automated_test: boolean;
				/**
				 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
				 */
				is_manual_test: boolean;
				/**
				 * Whether the event is from a test
				 */
				is_test?: boolean;
				/**
				 * The hashed IP address of the client
				 */
				ip_address_hash: string;
				/**
				 * The type of IP address of the client
				 */
				ip_address_type: 'ipv4' | 'ipv6';
				/**
				 * Parent account ID
				 */
				member_id?: string;
				/**
				 * User account ID
				 */
				user_id?: string;
				/**
				 * Session ID for the given user's session
				 */
				session_id: string;
				/**
				 * Information about the page the user is on at the time of the given event
				 */
				page_info: {
					/**
					 * The URL of the page the user is on at the time of the given event excluding any query parameters
					 */
					page_url: string;
					/**
					 * The semantic name of the page the user is on at the time of the given event
					 */
					page_name: string;
					/**
					 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
					 */
					page_version?: string;
					/**
					 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
					 */
					previous_page_url?: string;
					/**
					 * The URL of the page the user came from to get to the given page
					 */
					referrer?: string;
					/**
					 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
					 */
					url_params?: {};
					/**
					 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
					 */
					funnel_name: string;
					/**
					 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
					 */
					funnel_step?: number;
					/**
					 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
					 */
					funnel_pages?: {};
					[k: string]: unknown;
				};
				[k: string]: unknown;
			};
			server?: {
				hostname?: string;
				/**
				 * Sever timestamp in milliseconds
				 */
				timestamp?: number;
				[k: string]: unknown;
			};
			kinesis?: {
				/**
				 * Kinesis timestamp in milliseconds
				 */
				timestamp?: number;
				[k: string]: unknown;
			};
		};
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event fired when a route guard or policy denies a user access to a protected route or feature, and the user is redirected elsewhere. Used for product signals like 'cancelled user landed on reactivation page but isn't eligible' and 'no policy matched, defaulted to home'.
 */
export interface IClientAccessDenied {
	type: 'client.access_denied';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * Name of the policy that denied access (e.g., 'CANCELLED_USERS_ONLY', 'DEFAULT_POLICY_AUTHORIZER').
		 */
		policy: string;
		/**
		 * Short reason code (e.g., 'ineligible_for_reactivation', 'no_matching_policy', 'token_invalid').
		 */
		reason: string;
		/**
		 * User's account status at the time of denial, when available.
		 */
		account_status?: string;
		/**
		 * Whether the user's auth token was valid at the time of denial, when available.
		 */
		token_valid?: boolean;
		/**
		 * Eligibility flag the policy evaluated, when available.
		 */
		is_eligible?: boolean;
		/**
		 * Path the user was redirected to.
		 */
		destination?: string;
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event fired when tracking campaign attribution. Captures campaign source information.
 */
export interface IClientCampaignClick {
	type: 'client.campaign_click';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * Campaign source tag identifier
		 */
		source_tag: string;
		/**
		 * Dynamic campaign parameters (flexible object) such as UTM parameters
		 */
		dynamic_info: {};
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event fired when a user attempts to cancel their subscription, regardless of whether the cancel call succeeded.
 */
export interface IClientCancelAttempt {
	type: 'client.cancel_attempt';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * Whether the cancel API call succeeded.
		 */
		outcome: 'success' | 'failure';
		/**
		 * Short error reason when outcome is 'failure'. Must not contain PII.
		 */
		error_message?: string;
		/**
		 * Category of subscription being cancelled (e.g., 'monthly', 'annual', 'trial').
		 */
		subscription_category?: string;
		/**
		 * If the user arrived from a native/web app, the app identifier.
		 */
		referring_app?: string;
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event fired when a client-side data integrity anomaly is detected (e.g., user has multiple primary products, failsafe assignment kicked in, malformed payload at a known boundary). Distinct from generic errors: this is for data-shape signals worth analyzing across users.
 */
export interface IClientDataAnomaly {
	type: 'client.data_anomaly';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * Categorical type of anomaly (e.g., 'failsafe_primary_product_assignment', 'multiple_primary_products').
		 */
		kind: string;
		/**
		 * Human-readable description of the anomaly. Must not contain PII.
		 */
		message?: string;
		/**
		 * Anomaly-specific structured context. Must not contain PII or raw payment data.
		 */
		context?: {
			[k: string]: unknown;
		};
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event fired when a user login attempt completes, regardless of outcome. Replaces an ad-hoc trackClientDebug call that included the full user store payload; this schema deliberately constrains the payload to non-PII fields.
 */
export interface IClientLoginComplete {
	type: 'client.login_complete';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * Whether the login attempt succeeded.
		 */
		status: 'success' | 'failure';
		/**
		 * Path the user was redirected to after the login attempt.
		 */
		redirect_path?: string;
		/**
		 * Short reason code when status is 'failure'. Must not contain PII.
		 */
		failure_reason?: string;
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event fired when a user is navigated out of the marketing web client into a downstream destination (native app, web app, external download URL). Funnel-handoff signal for measuring marketing → product conversion.
 */
export interface IClientMarketingExit {
	type: 'client.marketing_exit';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * Class of destination the user is being sent to.
		 */
		destination_type: 'native_app' | 'web_app' | 'external_download' | 'external_url';
		/**
		 * Sanitized URL or deep-link the user is being sent to. MUST NOT include short-lived auth tokens, session tokens, or other secrets — callers must redact those before logging (e.g., replace the token segment with '{token}').
		 */
		destination: string;
		/**
		 * Service or component that initiated the exit (e.g., 'DownloadAppService.download2_0App', 'FlutterAppService.navigateToNativeApp', 'homeApp.navigateToSubscription').
		 */
		source: string;
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event fired when a user clicks a link anywhere on the browser.
 */
export interface IClientPageLinkClick {
	type: 'client.page_link_click';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * Identifier of the clicked element
		 */
		element_id: string;
		/**
		 * DOM path to the element (may be verbose)
		 */
		element_path?: string;
		/**
		 * The target of the href attribute
		 */
		href_target?: string;
		/**
		 * The text of the link
		 */
		link_text?: string;
		/**
		 * The type of link
		 */
		link_type?: string;
		/**
		 * The coordinates of the link on the page
		 */
		link_coordinates?: {
			/**
			 * The x coordinate of the link
			 */
			x?: number;
			/**
			 * The y coordinate of the link
			 */
			y?: number;
		};
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event fired when a user visits a page in the browser.
 */
export interface IClientPageLoad {
	type: 'client.page_load';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * The title of the page
		 */
		page_title?: string;
		/**
		 * Whether this is the initial page load
		 */
		is_initial_page_load?: boolean;
		/**
		 * Origin URL (where the navigation came from)
		 */
		referrer?: string;
		/**
		 * The name of the page which the user is navigating to
		 */
		page_name: string;
		/**
		 * Whether the page-load resolved its required data successfully. Absent on initial page-load events that don't have a notion of success/failure; populated by routes that perform data fetches in their load function (e.g., parent-section, support-chatbot).
		 */
		load_status?: 'success' | 'failure';
		/**
		 * Short error reason when load_status is 'failure' (e.g., 'auth-error', 'subscription-load-error'). Must not contain PII.
		 */
		load_error?: string;
		/**
		 * If the user arrived from a native/web app, the app identifier. Used by in-app load-status events to attribute outcomes to the source app.
		 */
		referring_app?: string;
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event fired when the snapped scroll-depth mark changes. web-client-classic reports every 10% (10, 20, 30, …) in either direction, with no maximum depth, and reports a mark again when the visitor crosses it again. The parent center reports every 2% (2, 4, 6, …), only while depth is increasing, and does not repeat a mark. Clients that track page sections also report which sections are on screen at the mark and which have been seen so far on the page.
 */
export interface IClientPageScroll {
	type: 'client.page_scroll';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * The scroll-depth mark just entered, snapped down from the raw scroll percent to the client's mark spacing. Not the continuous percent. There is no maximum. web-client-classic uses a 10% spacing (17 reports 10) and emits when that mark changes, scrolling down or up, including a mark already reported. The parent center uses a 2% spacing (3 reports 2, 45 reports 44), emits only while depth is increasing, and does not repeat a mark.
		 */
		percentage_scrolled: number;
		/**
		 * Ids of the page sections (elements tagged `data-track-section`) on screen when this mark was crossed, in document order. A section counts as on screen when at least 50% of its own height is visible, or when it fills at least 50% of the viewport (so a section taller than the viewport can still qualify). Hidden, zero-height sections never count. Empty array when no tagged section qualifies. Measured when the snapped mark changes, including when the visitor scrolls back up, not on every scroll event. Omitted by clients that do not tag sections.
		 */
		sections_in_viewport?: string[];
		/**
		 * Ids of every page section that has appeared in `sections_in_viewport` so far on the current page, in the order each was first seen. Cumulative and free of duplicates; includes the sections in this event's `sections_in_viewport`. Resets when the user navigates to a new page. Omitted by clients that do not tag sections.
		 */
		sections_viewed?: string[];
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event fired at each stage of a user's payment flow (initiation, authorization, submission, completion, cancellation, failure). Covers Apple Pay, Google Pay, PayPal, and Paddle; replaces ad-hoc trackClientDebug calls scattered across payment service code and route handlers.
 */
export interface IClientPaymentFlowState {
	type: 'client.payment_flow_state';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * Payment provider the event relates to.
		 */
		provider: 'apple_pay' | 'google_pay' | 'paypal' | 'paddle';
		/**
		 * Stage of the payment flow this event represents.
		 */
		stage:
			| 'availability_checked'
			| 'session_failed'
			| 'checkout_initiated'
			| 'checkout_loaded'
			| 'checkout_updated'
			| 'merchant_validated'
			| 'authorized'
			| 'submitted'
			| 'completed'
			| 'cancelled'
			| 'failed'
			| 'method_blocked'
			| 'webhook_received';
		/**
		 * Product surface the flow is running on, when known. Absent on service-level events (SDK availability check, session-error, oncancel) where the user hasn't reached a specific payment surface yet.
		 */
		surface?:
			| 'subscription'
			| 'subscription_offer'
			| 'in_app_subscription'
			| 'starter_pack'
			| 'gift'
			| 'assessment_purchase'
			| 'tutor_offer';
		/**
		 * Outcome of the stage when applicable (e.g., availability_checked can succeed or fail; completed can succeed or fail).
		 */
		outcome?: 'success' | 'failure';
		/**
		 * Time the stage took, in milliseconds. Used for performance stages (checkout_loaded, checkout_updated).
		 */
		duration_ms?: number;
		/**
		 * Provider- or app-specific error code when stage failed (e.g., 'sdk_timeout', 'prepaid_card', 'missing_data').
		 */
		error_code?: string;
		/**
		 * Human-readable error message when stage failed. Must not contain PII.
		 */
		error_message?: string;
		/**
		 * Provider transaction id when known (post-authorization or webhook).
		 */
		transaction_id?: string;
		/**
		 * Charge amount in the smallest currency unit, when known.
		 */
		amount?: number;
		/**
		 * ISO 4217 currency code when known.
		 */
		currency_code?: string;
		/**
		 * Provider-specific fields that don't generalize (e.g., apple_pay merchant_identifier, credential_status; paddle webhook payload). Must not contain PII or raw card data.
		 */
		provider_details?: {
			[k: string]: unknown;
		};
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event fired each time a user has loaded a page that contains available products to purchase.
 */
export interface IClientProductAvailability {
	type: 'client.product_availability';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * An array of the available subscription plans available to the user at the given point in time.
		 */
		available_subscription_plans: unknown[];
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event fired when the user's active product experience context switches (e.g., navigating between ABCmouse, ABCmouse for Schools, etc.). Replaces a trackClientDebug call in ProductExperienceService.
 */
export interface IClientProductExperienceSwitch {
	type: 'client.product_experience_switch';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * Product the user was on before the switch. Absent if the user had no active product before.
		 */
		original_product?: string;
		/**
		 * Product the user is on after the switch.
		 */
		new_product: string;
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event fired when a user visits via a QR code. Tracks QR code scan attribution.
 */
export interface IClientQrCodeVisit {
	type: 'client.qr_code_visit';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * QR code specific information
		 */
		qr_info: {
			/**
			 * QR code source/origin
			 */
			source?: string;
			/**
			 * QR code identifier/token
			 */
			token: string;
		};
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event fired when the web client application initializes. Contains shared metadata that is stable within a session and referenced by other events to avoid payload duplication.
 */
export interface IClientSessionInit {
	type: 'client.session_init';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * Active client-side experiments/variations
		 */
		active_client_experiments?: {};
		/**
		 * Advertising identifier for attribution tracking
		 */
		adid?: string;
		/**
		 * Information specific to the app that is being initialized
		 */
		app_info: {
			/**
			 * Version of the web client application
			 */
			app_version: string;
			/**
			 * Application name (e.g., 'ABCmouse Web')
			 */
			app_name: string;
			/**
			 * Build number or commit hash from CI/CD
			 */
			app_build_number?: string;
			/**
			 * Deployment environment
			 */
			environment?: 'prod' | 'staging' | 'dev' | 'local';
			/**
			 * Frontend framework (e.g., 'sveltekit', 'react')
			 */
			framework?: string;
			/**
			 * Version of the frontend framework
			 */
			framework_version?: string;
			/**
			 * Application store (e.g., 'appstore', 'playstore')
			 */
			app_store?:
				| 'amazon app store'
				| 'apple app store'
				| 'google play store'
				| 'samsung app store'
				| 'browser';
		};
		/**
		 * Information about the device and browser
		 */
		device_info: {
			/**
			 * Browser user agent string
			 */
			user_agent?: string;
			/**
			 * Browser platform (navigator.platform)
			 */
			platform?: string;
			/**
			 * Browser vendor (navigator.vendor)
			 */
			vendor?: string;
			/**
			 * Name of the browser (parsed from user agent)
			 */
			browser_name?: string;
			/**
			 * Version of the browser (parsed from user agent)
			 */
			browser_version?: string;
			/**
			 * Type of device (parsed from user agent)
			 */
			device_type?: 'desktop' | 'phone' | 'tablet' | 'console' | 'smarttv';
			/**
			 * Operating system name (parsed from user agent)
			 */
			os_name?: string;
			/**
			 * Operating system version (parsed from user agent)
			 */
			os_version?: string;
			/**
			 * Number of logical processors (navigator.hardwareConcurrency)
			 */
			cpu_cores?: number;
			/**
			 * Approximate device memory in gigabytes (navigator.deviceMemory)
			 */
			physical_memory?: number;
			/**
			 * Maximum number of simultaneous touch points (navigator.maxTouchPoints)
			 */
			max_touch_points?: number;
			/**
			 * Viewport width in pixels (window.innerWidth)
			 */
			viewport_width?: number;
			/**
			 * Viewport height in pixels (window.innerHeight)
			 */
			viewport_height?: number;
			/**
			 * Screen width in pixels (screen.width)
			 */
			screen_width?: number;
			/**
			 * Screen height in pixels (screen.height)
			 */
			screen_height?: number;
			/**
			 * Screen color depth in bits (screen.colorDepth)
			 */
			color_depth?: number;
			/**
			 * Device pixel ratio (window.devicePixelRatio)
			 */
			pixel_ratio?: number;
			/**
			 * Always true for web browsers
			 */
			is_physical_device?: true;
			/**
			 * Browser locale (navigator.language)
			 */
			locale?: string;
			/**
			 * Array of preferred languages (navigator.languages)
			 */
			languages?: string[];
		};
		/**
		 * Active feature flags for the session
		 */
		feature_flags?: {
			[k: string]: boolean | string | number;
		};
		/**
		 * Geographic information for the user (SERVER-SIDE ONLY: requires CloudFront headers or GeoIP lookup)
		 */
		geo_info: {
			/**
			 * ISO 2-letter country code (e.g., 'US') - from CloudFront-Viewer-Country header
			 */
			country_code?: string;
			/**
			 * ISO 3-letter country code - from server GeoIP lookup
			 */
			country_code3?: string;
			/**
			 * Full country name - from server GeoIP lookup
			 */
			country_name?: string;
			/**
			 * State/region name - from server GeoIP lookup
			 */
			region?: string;
			/**
			 * State/region code - from server GeoIP lookup
			 */
			area_code?: string;
			/**
			 * City name - from server GeoIP lookup
			 */
			city?: string;
			/**
			 * Postal/ZIP code - from server GeoIP lookup
			 */
			postal_code?: string;
			/**
			 * Latitude coordinate - from server GeoIP lookup
			 */
			latitude?: string;
			/**
			 * Longitude coordinate - from server GeoIP lookup
			 */
			longitude?: string;
			/**
			 * IANA timezone (e.g., 'America/Phoenix') - from server GeoIP lookup
			 */
			time_zone?: string;
			/**
			 * Continent code (e.g., 'NA') - from server GeoIP lookup
			 */
			continent_code?: string;
			/**
			 * DMA/metro code - from server GeoIP lookup
			 */
			dma_code?: string;
		};
		/**
		 * For web: browser name and version (e.g., 'Chrome 120.0'). Native apps would have device model.
		 */
		marketing_name?: string;
		/**
		 * User settings and preferences
		 */
		settings_info: {
			/**
			 * User's locale/language preference (e.g., 'en_US') - from navigator.language
			 */
			locale: string;
			/**
			 * User's timezone - from Intl.DateTimeFormat().resolvedOptions().timeZone
			 */
			timezone?: string;
			/**
			 * Array of preferred languages - from navigator.languages
			 */
			languages?: string[];
		};
		/**
		 * Whether the session originated from a link in a native app
		 */
		is_link_from_app?: boolean;
		/**
		 * Platform of the app that linked to this session (ios, android, etc.)
		 */
		link_from_app_platform?: string;
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Base event specification for application events. These fields are on every event
 */
export interface IEventManifest {
	type: 'event_manifest';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event fired when client-side form is submitted. Tracks successes and validation errors.
 */
export interface IFormSubmission {
	type: 'form.submission';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * Identifier of the form element. Example: form-create-account
		 */
		element_id?: string;
		/**
		 * Type of the form element
		 */
		element_type?: string;
		/**
		 * Array of component information
		 */
		component_info_array?: {
			/**
			 * Identifier of the component
			 */
			component_id?: string;
			/**
			 * Array of validation error messages
			 */
			errors?: string[];
		}[];
		/**
		 * Whether the form as a whole passed validation
		 */
		valid?: boolean;
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event for sending conversion data to third-party platforms (Facebook, Google, etc.). Sent via direct API call.
 */
export interface IThirdPartyConversion {
	type: 'third_party.conversion';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * Unique identifier for this conversion event
		 */
		conversion_event_id: string;
		/**
		 * User's country code
		 */
		country_code?: string;
		/**
		 * Flexible conversion metadata object
		 */
		event_info?: {};
		/**
		 * Array of third-party platforms receiving this conversion (e.g., ['facebook', 'google'])
		 */
		event_source: string[];
		/**
		 * URL where the conversion originated
		 */
		event_source_url?: string;
		/**
		 * Type of conversion (e.g., 'subscribe', 'initiate_checkout', 'purchase')
		 */
		event_type: string;
		/**
		 * Normalized (Trimmed and lowercase) SHA-256 hashed email address of the user with @relay.aofl.io added to the end.
		 */
		relay_email?: string;
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event for tracking interactions emitted by third-party payment/checkout SDKs (e.g. Apple Pay, PayPal). Captures on approve, on cancel, and on error flows so we can measure SDK usage and outcomes.
 */
export interface IThirdPartyWebSdkEvent {
	type: 'third_party.web_sdk_event';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * Event type identifier for the specific SDK event (approve, cancel, error, etc...).
		 */
		third_party_event_type: string;
		/**
		 * Name of the third-party SDK that emitted the interaction (e.g. ApplePay, PayPal, etc.)
		 */
		third_party_name: string;
		/**
		 * Machine-readable status for the interaction (e.g. cancelled, approved, failed)
		 */
		status?: string;
		/**
		 * Human-readable message from the SDK or client (e.g. 'User cancelled Apple Pay transaction', 'Payment failed - insufficient funds')
		 */
		message?: string;
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event fired when a UI element is shown to the user (e.g., popup, modal, banner, tooltip, notification)
 */
export interface IUiShow {
	type: 'ui.show';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * Unique identifier of the UI element shown
		 */
		element_id: string;
		/**
		 * Type of UI element (e.g., 'popup', 'modal', 'banner', 'video', 'tooltip', 'notification')
		 */
		element_type: string;
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event fired when a video is completed in the browser (either auto-played or manually played)
 */
export interface IVideoComplete {
	type: 'video.complete';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * The type of play that occurred (auto-played or manually played)
		 */
		video_play_type: 'autoplay' | 'manual';
		/**
		 * The percentage of the video that was watched (0-100)
		 */
		percent_watched: number;
		/**
		 * The length of the video in seconds
		 */
		video_length_seconds: number;
		/**
		 * The name of the video
		 */
		video_name: string;
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event fired when a video is paused in the browser (either auto-played or manually played)
 */
export interface IVideoPause {
	type: 'video.pause';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * The type of play that occurred (auto-played or manually played)
		 */
		video_play_type: 'autoplay' | 'manual';
		/**
		 * The position of the video in seconds when it was paused
		 */
		video_position?: number;
		/**
		 * The length of the video in seconds
		 */
		video_length_seconds: number;
		/**
		 * The name of the video
		 */
		video_name: string;
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Fires when a user seeks a particular position in a video on browser
 */
export interface IVideoSeek {
	type: 'video.seek';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * The type of play that occurred (auto-played or manually played)
		 */
		video_play_type: 'autoplay' | 'manual';
		/**
		 * The length of the video in seconds
		 */
		video_length_seconds?: number;
		/**
		 * The name of the video
		 */
		video_name: string;
		/**
		 * Where the user started from during the seek
		 */
		video_position_start: number;
		/**
		 * Where the user ended up in the video after the seek
		 */
		video_position_end: number;
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event fired when a video is started in the browser (either auto-played or manually played)
 */
export interface IVideoStart {
	type: 'video.start';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * The type of play that occurred (auto-played or manually played)
		 */
		video_play_type: 'autoplay' | 'manual';
		/**
		 * The length of the video in seconds
		 */
		video_length_seconds: number;
		/**
		 * The name of the video
		 */
		video_name: string;
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Event fired when a video is unpaused in the browser (either auto-played or manually played)
 */
export interface IVideoUnpause {
	type: 'video.unpause';
	/**
	 * uuid of the event, generated by the event logger
	 */
	uuid: string;
	/**
	 * Client timestamp in milliseconds
	 */
	timestamp: string;
	/**
	 * Client device_id
	 */
	device_id: string;
	/**
	 * Version of the event format
	 */
	specversion: string;
	/**
	 * A dot delimited name for the type of event
	 */
	event_type: string;
	/**
	 * The pipeline or product name where the event is being fired from
	 */
	source: string;
	/**
	 * The source version (app version / api version)
	 */
	source_version: string;
	/**
	 * The pipeline
	 */
	pipeline: string;
	data: {
		/**
		 * The AOFL product that the user is using
		 */
		aofl_product: string;
		/**
		 * The type of play that occurred (auto-played or manually played)
		 */
		video_play_type: 'autoplay' | 'manual';
		/**
		 * The position of the video in seconds when it was unpaused
		 */
		video_position?: number;
		/**
		 * The length of the video in seconds
		 */
		video_length_seconds: number;
		/**
		 * The name of the video
		 */
		video_name: string;
		[k: string]: unknown;
	};
	/**
	 * The pipeline context for the event
	 */
	pipeline_context: {
		client: {
			/**
			 * The platform of the client
			 */
			client_platform?: string;
			/**
			 * Whether the event is from an automated test
			 */
			is_automated_test: boolean;
			/**
			 * Whether the event is from a manual test.  This can be detected by checking for the presence of the office IP address
			 */
			is_manual_test: boolean;
			/**
			 * Whether the event is from a test
			 */
			is_test?: boolean;
			/**
			 * The hashed IP address of the client
			 */
			ip_address_hash: string;
			/**
			 * The type of IP address of the client
			 */
			ip_address_type: 'ipv4' | 'ipv6';
			/**
			 * Parent account ID
			 */
			member_id?: string;
			/**
			 * User account ID
			 */
			user_id?: string;
			/**
			 * Session ID for the given user's session
			 */
			session_id: string;
			/**
			 * Information about the page the user is on at the time of the given event
			 */
			page_info: {
				/**
				 * The URL of the page the user is on at the time of the given event excluding any query parameters
				 */
				page_url: string;
				/**
				 * The semantic name of the page the user is on at the time of the given event
				 */
				page_name: string;
				/**
				 * An identifier for the specific version of the page the user is on at the time of the given event.  This can be used to track changes to the page over time and how that impacts the user's experience.
				 */
				page_version?: string;
				/**
				 * The URL of the page the user was on immediately before this event (includes in-app SPA navigations). Query string and hash are omitted.
				 */
				previous_page_url?: string;
				/**
				 * The URL of the page the user came from to get to the given page
				 */
				referrer?: string;
				/**
				 * Query parameters from the URL as key-value pairs (e.g., {'utm_src': 'test'})
				 */
				url_params?: {};
				/**
				 * The name of the funnel the user is in at the time of the given event (Example: subscription flow, forgot password flow, reactivation flow, special offer flow)
				 */
				funnel_name: string;
				/**
				 * The step of the funnel the user is in at the time of the given event based on the number of pages in that particular flow at the time of the event (Example: 1, 2, 3)
				 */
				funnel_step?: number;
				/**
				 * An object of the pages within the funnel at the time of the given event (Example: [{page_name: 'page 1', page_url: 'https://www.example.com/page1', 'step': 1}, {page_name: 'page 2', page_url: 'https://www.example.com/page2', 'step': 2}])
				 */
				funnel_pages?: {};
				[k: string]: unknown;
			};
			[k: string]: unknown;
		};
		server?: {
			hostname?: string;
			/**
			 * Sever timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
		kinesis?: {
			/**
			 * Kinesis timestamp in milliseconds
			 */
			timestamp?: number;
			[k: string]: unknown;
		};
	};
}

/**
 * Wire-format event-type strings keyed by SCREAMING_SNAKE_CASE
 * identifier. Import this map to reference an event type without
 * hard-coding the wire string at the call site.
 */
export const EVENT_TYPES = {
	ACCOUNT_SETTINGS_UPDATE: 'account.settings_update',
	ACCOUNT_USER_CREATE: 'account.user_create',
	BASE_EVENT: 'base_event',
	CLIENT_ACCESS_DENIED: 'client.access_denied',
	CLIENT_CAMPAIGN_CLICK: 'client.campaign_click',
	CLIENT_CANCEL_ATTEMPT: 'client.cancel_attempt',
	CLIENT_DATA_ANOMALY: 'client.data_anomaly',
	CLIENT_LOGIN_COMPLETE: 'client.login_complete',
	CLIENT_MARKETING_EXIT: 'client.marketing_exit',
	CLIENT_PAGE_LINK_CLICK: 'client.page_link_click',
	CLIENT_PAGE_LOAD: 'client.page_load',
	CLIENT_PAGE_SCROLL: 'client.page_scroll',
	CLIENT_PAYMENT_FLOW_STATE: 'client.payment_flow_state',
	CLIENT_PRODUCT_AVAILABILITY: 'client.product_availability',
	CLIENT_PRODUCT_EXPERIENCE_SWITCH: 'client.product_experience_switch',
	CLIENT_QR_CODE_VISIT: 'client.qr_code_visit',
	CLIENT_SESSION_INIT: 'client.session_init',
	EVENT_MANIFEST: 'event_manifest',
	FORM_SUBMISSION: 'form.submission',
	THIRD_PARTY_CONVERSION: 'third_party.conversion',
	THIRD_PARTY_WEB_SDK_EVENT: 'third_party.web_sdk_event',
	UI_SHOW: 'ui.show',
	VIDEO_COMPLETE: 'video.complete',
	VIDEO_PAUSE: 'video.pause',
	VIDEO_SEEK: 'video.seek',
	VIDEO_START: 'video.start',
	VIDEO_UNPAUSE: 'video.unpause',
} as const;

/**
 * Map of every known event-type wire string to its typed
 * interface. Derived from the union of all event interfaces so
 * the wire strings live only on each interface's `type` literal.
 * Pass as the type parameter to createAnalytics<KnownEvents>().
 */
type KnownEventInterface =
	| IAccountSettingsUpdate
	| IAccountUserCreate
	| IBaseEvent
	| IClientAccessDenied
	| IClientCampaignClick
	| IClientCancelAttempt
	| IClientDataAnomaly
	| IClientLoginComplete
	| IClientMarketingExit
	| IClientPageLinkClick
	| IClientPageLoad
	| IClientPageScroll
	| IClientPaymentFlowState
	| IClientProductAvailability
	| IClientProductExperienceSwitch
	| IClientQrCodeVisit
	| IClientSessionInit
	| IEventManifest
	| IFormSubmission
	| IThirdPartyConversion
	| IThirdPartyWebSdkEvent
	| IUiShow
	| IVideoComplete
	| IVideoPause
	| IVideoSeek
	| IVideoStart
	| IVideoUnpause;
export type KnownEvents = {
	[E in KnownEventInterface as E['type']]: E;
};
